import { createTheme } from '@mui/material';

/**
 * Tokki design tokens.
 *
 * The identity (warm cream, dusty rose, Onest, soft radii) is the incumbent one.
 * What changed is that it now lives in one place instead of being retyped as raw
 * hex at every call site.
 */

export const tokens = {
  color: {
    // The brand rose. Fine as a *surface*, fails contrast as text on light.
    rose: '#D6A79B',
    roseHover: '#C9917F',
    // Darkened rose for text/icons on light surfaces (4.8:1 on white).
    roseInk: '#A85F4C',
    roseDeep: '#7A4335',
    // Tinted rose surfaces, for hover beds and selected states.
    roseWash: '#F6EBE6',

    cream: '#F7F3EF',
    creamDeep: '#EFE7E0',
    white: '#FFFFFF',

    // Warm near-black, never pure #000 against a cream ground.
    ink: '#2E2422',
    // Warm-tinted secondary (6.5:1 on white) — tinted from the ground, not gray.
    inkMuted: '#6B5A54',
    inkFaint: '#8B7A73',

    line: '#E7DED7',

    // The footer ground. Carries white text at 6.9:1.
    cocoa: '#785043',
    cocoaLine: 'rgba(255, 255, 255, 0.18)',
  },

  radius: {
    sm: 10,
    md: 16,
    lg: 24,
    pill: 999,
  },

  // Strong curves. The built-in CSS easings are too weak to read as intentional.
  ease: {
    out: 'cubic-bezier(0.23, 1, 0.32, 1)',
    inOut: 'cubic-bezier(0.77, 0, 0.175, 1)',
  },

  duration: {
    press: '140ms',
    control: '200ms',
    surface: '280ms',
    reveal: '620ms',
  },

  // Offset + soft blur, warm-tinted from the ground rather than neutral black.
  shadow: {
    sm: '0 1px 2px rgba(60, 40, 34, 0.06), 0 2px 8px rgba(60, 40, 34, 0.05)',
    md: '0 2px 4px rgba(60, 40, 34, 0.06), 0 8px 24px rgba(60, 40, 34, 0.08)',
    lg: '0 4px 8px rgba(60, 40, 34, 0.07), 0 18px 48px rgba(60, 40, 34, 0.11)',
  },

  // Measured at runtime by the header; used to offset sticky targets.
  headerHeight: { xs: 64, md: 80 },
} as const;

const theme = createTheme({
  palette: {
    // `rose` is the brand colour, but white text on it lands at 2.1:1 — it works
    // as a surface, not as a button that carries a label. `roseInk` is the same
    // hue taken far enough down to clear 4.5:1, so filled CTAs stay on-brand and
    // stay readable. Decorative rose lives on as `tokens.color.rose`.
    primary: {
      main: tokens.color.roseInk,
      light: tokens.color.rose,
      dark: tokens.color.roseDeep,
      contrastText: tokens.color.white,
    },
    background: {
      default: tokens.color.cream,
      paper: tokens.color.white,
    },
    text: {
      primary: tokens.color.ink,
      secondary: tokens.color.inkMuted,
    },
    divider: tokens.color.line,
  },

  shape: {
    borderRadius: tokens.radius.sm,
  },

  typography: {
    fontFamily: '"Onest", system-ui, -apple-system, "Segoe UI", sans-serif',

    // Display sizes fluid across breakpoints so real copy is tested at every width.
    // Tracking tightens as size grows — large type needs less air between letters.
    h1: {
      fontSize: 'clamp(2.5rem, 1.6rem + 3.8vw, 4.5rem)',
      fontWeight: 700,
      lineHeight: 1.05,
      letterSpacing: '-0.035em',
    },
    h2: {
      fontSize: 'clamp(2rem, 1.4rem + 2.6vw, 3.25rem)',
      fontWeight: 700,
      lineHeight: 1.12,
      letterSpacing: '-0.03em',
    },
    h3: {
      fontSize: 'clamp(1.65rem, 1.25rem + 1.7vw, 2.5rem)',
      fontWeight: 600,
      lineHeight: 1.18,
      letterSpacing: '-0.025em',
    },
    h4: {
      fontSize: 'clamp(1.35rem, 1.1rem + 1vw, 1.85rem)',
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.015em',
    },
    h6: {
      fontSize: '1.0625rem',
      fontWeight: 600,
      lineHeight: 1.35,
      letterSpacing: '-0.01em',
    },
    body1: {
      fontSize: '1.0625rem',
      lineHeight: 1.6,
      letterSpacing: '-0.005em',
    },
    body2: {
      fontSize: '0.9375rem',
      lineHeight: 1.55,
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        // One visible focus ring for the whole app. The previous code removed
        // focus outlines on the menu categories; this puts them back everywhere.
        '*:focus-visible': {
          outline: `2px solid ${tokens.color.roseInk}`,
          outlineOffset: 2,
          borderRadius: 4,
        },
        // Reduced motion means fewer and gentler animations, not zero. Opacity
        // and color still transition; movement does not.
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: tokens.radius.pill,
          fontWeight: 600,
          // Named properties only — `transition: all` animates layout props too.
          transition: `transform ${tokens.duration.press} ${tokens.ease.out}, background-color ${tokens.duration.control} ease, color ${tokens.duration.control} ease, box-shadow ${tokens.duration.control} ease`,
          // Instant feedback: the interface confirms it heard the press.
          '&:active': { transform: 'scale(0.97)' },
        },
        containedPrimary: {
          boxShadow: tokens.shadow.sm,
          '&:hover': { boxShadow: tokens.shadow.md },
        },
        sizeLarge: {
          padding: '14px 30px',
          fontSize: '1rem',
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: `transform ${tokens.duration.press} ${tokens.ease.out}, background-color ${tokens.duration.control} ease`,
          '&:active': { transform: 'scale(0.94)' },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: tokens.radius.md },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: tokens.color.creamDeep },
      },
    },
  },
});

export default theme;
